'use client';

import { CardProps, Divider, Select, Snippet } from '@heroui/react';

import React from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Tabs,
  Tab,
  ScrollShadow,
  CardFooter,
  SelectItem,
  Pagination,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import CopyIc from './Copy.svg';
import NotificationItem from './notification-item';
import Typography from '../typography/Typography';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslations } from '../../contexts/translation.context';
import { clientNotificationService, partnerNotificationService } from '../../api';

interface Notification {
  id: string;
  created_at: string;
  message: string;
  alert_message: string | null;
  was_read: boolean;
  owner: string;
}

enum NotificationTabs {
  All = 'all',
  Unread = 'unread',
  Read = 'read',
}

export default function Component(props: CardProps) {
  const {
    notifications,
    getNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    user,
  } = useAuth();
  const { t } = useTranslations();
  const [activeTab, setActiveTab] = React.useState<NotificationTabs>(NotificationTabs.All);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageSize, setCurrentPageSize] = React.useState(10);
  const [wasReadFilter, setWasReadFilter] = React.useState<boolean | null | undefined>(undefined);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [readCount, setReadCount] = React.useState(0);

  const isPartner = window.location.pathname.startsWith('/partner');
  const botUsername = '@onlyu_service_bot';
  const userRole = isPartner ? 'partner' : 'client';
  const telegramBotUrl = user?.id
    ? `https://t.me/onlyu_service_bot?start=${user.id}_${userRole}`
    : 'https://t.me/onlyu_service_bot';
  const snippetTooltip = t('notificationsCard.snippetTooltip');
  const notificationsTitle = t('notificationsCard.notificationsTitle');
  const markAll = t('notificationsCard.markAll');
  const tabsAriaLabel = t('notificationsCard.tabsAriaLabel');
  const tabUnread = t('notificationsCard.tabUnread');
  const tabRead = t('notificationsCard.tabRead');
  const tabAll = t('notificationsCard.tabAll');
  const listAriaLabel = t('notificationsCard.listAria');
  const noNotificationsTitle = t('notificationsCard.noNotificationsTitle');
  const noNotificationsDescription = t('notificationsCard.noNotificationsDescription');
  const perPage = t('notificationsCard.perPage');
  const pageSizeAria = t('notificationsCard.pageSizeAria');
  const systemName = t('notificationsCard.systemName');
  const telegramTitle = t('notificationsCard.telegramTitle');
  const telegramKey = t('notificationsCard.telegramKey');
  const formatCountLabel = React.useCallback(
    (key: string, count: number) => t(key).replace('{count}', count.toString()),
    [t]
  );

  // Мемоизируем функции для предотвращения лишних ререндеров
  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = React.useCallback((keys: any) => {
    const newPageSize = parseInt(Array.from(keys)[0] as string);
    setCurrentPageSize(newPageSize);
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении размера
  }, []);

  // Функция для получения счетчиков уведомлений
  const fetchNotificationCounts = React.useCallback(async () => {
    try {
      const notificationService = isPartner
        ? partnerNotificationService
        : clientNotificationService;

      // Получаем счетчик непрочитанных
      const unreadResponse = await notificationService.getNotifications(1, 1, false);
      setUnreadCount(unreadResponse.data.count || 0);

      // Получаем счетчик прочитанных
      const readResponse = await notificationService.getNotifications(1, 1, true);
      setReadCount(readResponse.data.count || 0);
    } catch (error) {
      console.error('Error fetching notification counts:', error);
    }
  }, [isPartner]);

  const handleMarkAllAsRead = React.useCallback(async () => {
    await markAllNotificationsAsRead();
    // Обновляем счетчики после пометки всех как прочитанных
    fetchNotificationCounts();
    // Обновляем текущий список уведомлений
    getNotifications(currentPage, currentPageSize, wasReadFilter);
  }, [
    markAllNotificationsAsRead,
    fetchNotificationCounts,
    currentPage,
    currentPageSize,
    wasReadFilter,
  ]);

  const handleMarkAsRead = React.useCallback(
    async (id: string) => {
      await markNotificationAsRead(id);
      // Обновляем счетчики после пометки одного как прочитанного
      fetchNotificationCounts();
      // Обновляем текущий список уведомлений
      getNotifications(currentPage, currentPageSize, wasReadFilter);
    },
    [markNotificationAsRead, fetchNotificationCounts, currentPage, currentPageSize, wasReadFilter]
  );

  // Обработчик смены табов
  const handleTabChange = React.useCallback((selected: NotificationTabs) => {
    setActiveTab(selected);
    setCurrentPage(1); // Сбрасываем на первую страницу при смене таба

    // Устанавливаем фильтр was_read в зависимости от выбранного таба
    let newWasReadFilter: boolean | null | undefined;
    switch (selected) {
      case NotificationTabs.Unread:
        newWasReadFilter = false;
        break;
      case NotificationTabs.Read:
        newWasReadFilter = true;
        break;
      case NotificationTabs.All:
      default:
        newWasReadFilter = undefined;
        break;
    }
    setWasReadFilter(newWasReadFilter);
  }, []);

  // Загружаем уведомления при изменении страницы, размера страницы или фильтра
  React.useEffect(() => {
    getNotifications(currentPage, currentPageSize, wasReadFilter);
    fetchNotificationCounts(); // Обновляем счетчики при изменении данных
  }, [currentPage, currentPageSize, wasReadFilter, fetchNotificationCounts]);

  // Автоматическое обновление уведомлений и счетчиков каждые 10 секунд
  React.useEffect(() => {
    const interval = setInterval(() => {
      getNotifications(currentPage, currentPageSize, wasReadFilter);
      fetchNotificationCounts();
    }, 10000); // 10 секунд

    // Очистка интервала при размонтировании компонента
    return () => clearInterval(interval);
  }, [currentPage, currentPageSize, wasReadFilter, fetchNotificationCounts]);

  const totalPages = notifications ? Math.ceil(notifications.count / currentPageSize) : 0;
  const totalCount = notifications?.count || 0;
  const allCount = unreadCount + readCount;

  // Для отображения в Chip используем полученные счетчики
  const unreadChipLabel = formatCountLabel('notificationsCard.tabUnreadAria', unreadCount);
  const readChipLabel = formatCountLabel('notificationsCard.tabReadAria', readCount);
  const allChipLabel = formatCountLabel('notificationsCard.tabAllAria', allCount);

  return (
    <Card
      classNames={{
        base: 'min-w-[22.5rem] md:min-w-[27.4375rem] border border-light rounded-4',
      }}
      className="w-full py-2"
      {...props}
    >
      <CardHeader className="flex flex-col px-0 pb-0">
        <div className="flex w-full items-center justify-between px-4 py-3 pb-5">
          <div className="flex flex-col gap-1">
            <Typography variant="label1">{telegramTitle}</Typography>
            <Typography variant="body3" className="text-neutral-500">
              {t('notificationsCard.telegramDescription').split('{botLink}')[0]}
              <a
                href={telegramBotUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="!text-partner hover:underline"
              >
                {botUsername}
              </a>
              {t('notificationsCard.telegramDescription').split('{botLink}')[1]}
            </Typography>
          </div>
        </div>
        <Divider />
        <div className="flex flex-col md:gap-0 gap-4 md:pt-0 pt-2">
          <div className="px-4 py-2 md:p-4 w-full flex flex-wrap justify-between gap-1 md:gap-2.5 items-baseline">
            <Typography variant="label1" className="max-md:text-lg">
              {notificationsTitle}
            </Typography>
            <Typography
              variant="body2"
              className="!text-partner cursor-pointer hover:opacity-80"
              onClick={handleMarkAllAsRead}
            >
              {markAll}
            </Typography>
          </div>
          <div className="w-full justify-center items-center">
            <Tabs
              aria-label={tabsAriaLabel}
              classNames={{
                base: 'w-full',
                tabList:
                  'gap-0 gap-x-2 md:gap-6 px-4 md:px-6 py-0 w-full md:justify-center flex max-md:flex-wrap relative rounded-none border-b border-divider',
                cursor: isPartner ? 'w-full !bg-partner' : 'w-full',
                tab: 'max-w-fit px-1 md:px-2 md:h-12 max-md:pt-0 max-md:pb-3',
                tabContent: isPartner ? 'group-data-[selected=true]:text-partner' : undefined,
              }}
              color="primary"
              selectedKey={activeTab}
              variant="underlined"
              onSelectionChange={(selected) => handleTabChange(selected as NotificationTabs)}
            >
              <Tab
                key="unread"
                title={
                  <div className="flex items-center space-x-2">
                    <span>{tabUnread}</span>
                    <Chip
                      size="sm"
                      variant="flat"
                      aria-label={unreadChipLabel}
                      classNames={{
                        base: 'max-md:h-5 max-md:font-medium',
                      }}
                    >
                      {unreadCount}
                    </Chip>
                  </div>
                }
              />
              <Tab
                key="read"
                title={
                  <div className="flex items-center space-x-2">
                    <span>{tabRead}</span>
                    <Chip
                      size="sm"
                      variant="flat"
                      aria-label={readChipLabel}
                      classNames={{
                        base: 'max-md:h-5 max-md:font-medium',
                      }}
                    >
                      {readCount}
                    </Chip>
                  </div>
                }
              />
              <Tab
                key="all"
                title={
                  <div className="flex items-center space-x-2">
                    <span>{tabAll}</span>
                    <Chip
                      size="sm"
                      variant="flat"
                      aria-label={allChipLabel}
                      classNames={{
                        base: 'max-md:h-5 max-md:font-medium',
                      }}
                    >
                      {allCount}
                    </Chip>
                  </div>
                }
              />
            </Tabs>
          </div>
        </div>
      </CardHeader>
      <CardBody className="w-full gap-0 p-0">
        <ScrollShadow
          className="max-md:max-h-[21.875rem] md:h-[31.25rem] w-full"
          aria-label={listAriaLabel}
        >
          {notifications?.objects && notifications.objects.length > 0 ? (
            notifications.objects.map((notification: Notification) => (
              <NotificationItem
                key={notification.id}
                id={notification.id}
                isRead={notification.was_read}
                avatar="https://i.pravatar.cc/150?u=a04258114e29026708c"
                description={notification.message}
                name={systemName}
                time={new Date(notification.created_at).toLocaleDateString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                type="default"
                onMarkAsRead={() => handleMarkAsRead(notification.id)}
              />
            ))
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 py-6">
              <Icon
                className="text-default-400"
                icon="solar:bell-off-linear"
                width={40}
                aria-label={noNotificationsTitle}
              />
              <p className="text-small text-default-400">{noNotificationsDescription}</p>
            </div>
          )}
        </ScrollShadow>
      </CardBody>
      <CardFooter className="p-4">
        {notifications && notifications.count > 0 && (
          <div className="w-full flex items-center justify-between gap-1">
            <div className="flex items-center gap-2 md:gap-2.5">
              <Typography variant="body4" className="text-auth-muted text-xxs md:text-sm">
                {perPage}
              </Typography>
              <Select
                size="sm"
                selectedKeys={[currentPageSize.toString()]}
                onSelectionChange={handlePageSizeChange}
                className="w-20"
                aria-label={pageSizeAria}
              >
                <SelectItem key="10" textValue="10">
                  10
                </SelectItem>
                <SelectItem key="20" textValue="20">
                  20
                </SelectItem>
                <SelectItem key="50" textValue="50">
                  50
                </SelectItem>
              </Select>
            </div>

            <Pagination
              total={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              boundaries={-1}
              isCompact
              showControls
              color="primary"
              size="sm"
              classNames={
                isPartner
                  ? {
                      cursor: 'font-medium !bg-partner !text-white',
                      item: 'hover:!bg-gray-100',
                    }
                  : {}
              }
            />
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
